// UI hints for task actions. The server is the only authority (RF-FLOW-01): these tables
// only decide which requests to offer; every request can still be refused (gates,
// dependencies, versions) and the refusal reason is shown to the user.
import type { TaskAction, TaskCard, TaskColumn, TaskStatus } from "@maestros/contracts";

const CANDIDATES: Record<TaskStatus, TaskAction[]> = {
  backlog: ["start_analysis", "mark_ready", "block", "pause", "cancel"],
  in_analysis: ["mark_ready", "block", "pause", "cancel"],
  todo: ["start", "block", "wait_resource", "pause", "cancel"],
  in_progress: ["submit_for_testing", "complete", "block", "wait_resource", "pause", "fail", "cancel"],
  testing: ["gates_passed", "gates_failed", "block", "pause", "fail", "cancel"],
  in_review: ["review_approved", "review_rejected", "complete", "block", "pause", "cancel"],
  integrating: ["integrated", "complete", "block", "pause", "fail", "cancel"],
  awaiting_release: ["release_confirmed", "block", "pause", "cancel"],
  done: ["reopen"],
  blocked: ["unblock", "cancel"],
  waiting_resource: ["resource_available", "cancel"],
  paused: ["resume", "cancel"],
  failed: ["retry", "cancel"],
  cancelled: ["reopen"],
  // D23: decided from the approval card (approve/reject with the options the council prepared), never dragged.
  awaiting_human: ["cancel"],
};

/** Actions to offer: the server list (GET /v1/tasks/{id}) when loaded, else the hints. */
export function candidateActions(task: TaskCard, serverList?: readonly TaskAction[] | null): TaskAction[] {
  if (serverList) return serverList.filter((a) => a !== "admin_override");
  const list: TaskAction[] = CANDIDATES[task.status as TaskStatus] ?? [];
  return list.filter((a) => a !== "complete" || task.type === "investigation" || task.status === "integrating");
}

/** Action requested by dragging a card from its status to a target column/side state. */
export function actionForMove(task: TaskCard, target: TaskStatus): TaskAction | null {
  const from = task.status;
  if (from === target) return null;
  switch (target) {
    case "backlog":
      return from === "done" || from === "cancelled" ? "reopen" : leaveSide(task, target);
    case "in_analysis":
      return from === "backlog" ? "start_analysis" : leaveSide(task, target);
    case "todo":
      return from === "backlog" || from === "in_analysis" ? "mark_ready" : leaveSide(task, target);
    case "in_progress":
      if (from === "todo") return "start";
      if (from === "testing") return "gates_failed";
      if (from === "in_review") return "review_rejected";
      if (from === "failed") return "retry";
      return leaveSide(task, target);
    case "testing":
      return from === "in_progress" ? "submit_for_testing" : leaveSide(task, target);
    case "in_review":
      return from === "testing" ? "gates_passed" : leaveSide(task, target);
    case "integrating":
      return from === "in_review" ? "review_approved" : leaveSide(task, target);
    case "awaiting_release":
      return from === "integrating" ? "integrated" : leaveSide(task, target);
    case "done":
      if (from === "awaiting_release") return "release_confirmed";
      if (from === "integrating" || from === "in_progress" || from === "in_review") return "complete";
      return null;
    case "blocked":
      return "block";
    case "waiting_resource":
      return "wait_resource";
    case "paused":
      return "pause";
    case "failed":
      return "fail";
    case "cancelled":
      return "cancel";
    case "awaiting_human":
      return null;
  }
}

function leaveSide(task: TaskCard, target: TaskColumn): TaskAction | null {
  if (task.return_column !== target) return null;
  if (task.status === "blocked") return "unblock";
  if (task.status === "paused") return "resume";
  if (task.status === "waiting_resource") return "resource_available";
  return null;
}
